import React, { useEffect, useMemo, useRef, useState } from "react";
import { Building2, Check, Ship } from "lucide-react";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";
import { useResource } from "@/hooks/use-resource";
import { CompanyLogo } from "@/components/common";
import { Field, fieldCls } from "@/components/veiviser/WizardChrome";

/** A text field with quiet suggestions under it: arrow keys + Enter pick, typing on keeps the free text. */
export function Suggest({ value, onChange, onPick, options, render, placeholder, testId, autoFocus }) {
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const shown = options.slice(0, 7);
  const key = (e) => {
    if (!open || !shown.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, shown.length - 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); onPick(shown[hi]); setOpen(false); }
    if (e.key === "Escape") setOpen(false);
  };
  return (
    <div className="relative">
      <input value={value} onChange={(e) => { onChange(e.target.value); setOpen(true); setHi(0); }} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 120)} onKeyDown={key} placeholder={placeholder} autoFocus={autoFocus} autoComplete="off" className={fieldCls} data-testid={testId} />
      {open && shown.length ? (
        <ul className="absolute inset-x-0 top-[calc(100%+6px)] z-30 overflow-hidden rounded-[14px] border border-black/[0.07] bg-white p-1 shadow-float" data-testid={`${testId}-options`}>
          {shown.map((o, i) => (
            <li key={o.key}>
              <button type="button" onMouseDown={(e) => { e.preventDefault(); onPick(o); setOpen(false); }} onMouseEnter={() => setHi(i)} className={cn("flex w-full items-center gap-3 rounded-[10px] px-2.5 py-2 text-left text-[14px]", i === hi ? "bg-black/[0.045]" : "")} data-testid={`${testId}-option-${i}`}>{render(o)}</button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

const dims = (o) => [o.type, o.loa ? `${String(o.loa).replace(".", ",")} m` : null, o.built].filter(Boolean).join(" · ");

export function StepKunde({ v, set, setV }) {
  const { data: accounts } = useResource("/accounts", null);
  const [fleet, setFleet] = useState([]);
  const q = v.vessel.name.trim();
  const reqId = useRef(0);
  useEffect(() => {
    const id = ++reqId.current;
    const t = setTimeout(() => {
      api.get("/veiviser/fartoy", { params: { q, account_id: v.customer.account_id || undefined } }).then((r) => { if (id === reqId.current) setFleet(r.data || []); }).catch(() => {});
    }, 180);
    return () => clearTimeout(t);
  }, [q, v.customer.account_id]);

  const custOpts = useMemo(() => {
    const s = v.customer.name.trim().toLowerCase();
    return (accounts || []).filter((a) => !s || a.name.toLowerCase().includes(s)).filter((a) => a.name !== v.customer.name).map((a) => ({ key: a.id, ...a }));
  }, [accounts, v.customer.name]);
  const known = (accounts || []).find((a) => a.id === v.customer.account_id);
  const vesselOpts = fleet.filter((o) => o.name !== v.vessel.name && (!q || o.name.toLowerCase().includes(q.toLowerCase()))).map((o) => ({ key: o.name, ...o }));
  // The accounts can arrive after the name was typed (or restored from the draft): link an exact match as soon as they do.
  useEffect(() => {
    if (!accounts || v.customer.account_id || !v.customer.name.trim()) return;
    const hit = accounts.find((a) => a.name.trim().toLowerCase() === v.customer.name.trim().toLowerCase());
    if (hit) set("customer", { name: hit.name, account_id: hit.id });
  }, [accounts, v.customer.name, v.customer.account_id, set]);
  const typeCustomer = (name) => { const hit = (accounts || []).find((a) => a.name.trim().toLowerCase() === name.trim().toLowerCase()); set("customer", { name, account_id: hit?.id || null }); };
  const pickVessel = (o) => setV((cur) => ({ ...cur, vessel: { name: o.name, type: o.type || "", loa: o.loa ? String(o.loa) : "", beam: o.beam ? String(o.beam) : "" }, customer: cur.customer.name ? cur.customer : { name: o.customer || "", account_id: (accounts || []).find((a) => a.name === o.customer)?.id || null } }));

  return (
    <div className="space-y-9" data-testid="vv-kunde">
      <section className="space-y-4">
        <Field label="Kunde">
          <Suggest value={v.customer.name} onChange={typeCustomer} onPick={(a) => set("customer", { name: a.name, account_id: a.id })} options={custOpts} testId="vv-customer" autoFocus={!v.customer.name}
            placeholder="Rederi eller kunde, f.eks. Torghatten Nord AS" render={(a) => <><CompanyLogo name={a.name} domain={a.website} size="sm" className="h-7 w-7 rounded-lg" /><span className="min-w-0 flex-1 truncate font-medium">{a.name}</span><span className="text-[12px] text-muted-foreground">{a.city || a.country}</span></>} />
        </Field>
        {v.customer.name.trim() ? (
          <p className={cn("inline-flex items-center gap-2 text-[12.5px]", known ? "text-[hsl(var(--v-ok))]" : "text-muted-foreground")} data-testid="vv-customer-state">
            {known ? <><Check className="h-3.5 w-3.5" strokeWidth={2.6} /> Eksisterende kunde – saken kobles til kundekortet</> : <><Building2 className="h-3.5 w-3.5" /> Ny kunde – kundekortet opprettes sammen med saken</>}
          </p>
        ) : null}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Kontaktperson (valgfritt)"><input value={v.contact.name} onChange={(e) => set("contact.name", e.target.value)} placeholder="Navn" className={fieldCls} data-testid="vv-contact-name" /></Field>
          <Field label="E-post (valgfritt)"><input type="email" value={v.contact.email} onChange={(e) => set("contact.email", e.target.value)} placeholder="navn@rederi.no" className={fieldCls} data-testid="vv-contact-email" /></Field>
        </div>
      </section>
      <section className="space-y-4">
        <Field label="Fartøy">
          <Suggest value={v.vessel.name} onChange={(name) => set("vessel.name", name)} onPick={pickVessel} options={vesselOpts} testId="vv-vessel" placeholder="F.eks. MF Bømlo"
            render={(o) => <><span className="flex h-7 w-7 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))]"><Ship className="h-3.5 w-3.5 text-primary" /></span><span className="min-w-0 flex-1"><span className="block truncate font-medium">{o.name}</span><span className="block truncate text-[12px] text-muted-foreground">{dims(o)}{o.customer ? ` · ${o.customer}` : ""}</span></span>{o.known ? <span className="v-badge v-ok">Kjent</span> : null}</>} />
        </Field>
        {!q && vesselOpts.length && v.customer.account_id ? (
          <div className="flex flex-wrap gap-2" data-testid="vv-customer-vessels">
            {vesselOpts.slice(0, 6).map((o) => <button key={o.key} type="button" onClick={() => pickVessel(o)} className="inline-flex h-9 items-center gap-2 rounded-full border border-black/[0.08] bg-white px-3.5 text-[13px] font-medium text-foreground/75 transition-colors hover:border-black/[0.2] hover:text-foreground" data-testid={`vv-customer-vessel-${o.key}`}><Ship className="h-3.5 w-3.5 text-muted-foreground" />{o.name}</button>)}
          </div>
        ) : null}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <Field label="Type" className="col-span-2 sm:col-span-1"><input value={v.vessel.type} onChange={(e) => set("vessel.type", e.target.value)} placeholder="Bilferge" className={fieldCls} data-testid="vv-vessel-type" /></Field>
          <Field label="Lengde (m)"><input inputMode="decimal" value={v.vessel.loa} onChange={(e) => set("vessel.loa", e.target.value.replace(/[^\d.,]/g, ""))} placeholder="85" className={fieldCls} data-testid="vv-vessel-loa" /></Field>
          <Field label="Bredde (m)"><input inputMode="decimal" value={v.vessel.beam} onChange={(e) => set("vessel.beam", e.target.value.replace(/[^\d.,]/g, ""))} placeholder="16,5" className={fieldCls} data-testid="vv-vessel-beam" /></Field>
        </div>
        <p className="text-[12.5px] text-muted-foreground">Lengde og bredde skalerer standardtidene og avgjør om fartøyet går i flytedokka eller på slippen.</p>
      </section>
    </div>
  );
}
